import React, { useState, useMemo } from 'react';
import { useLanguage, translations, Language, setAppLanguage } from '../lib/i18n';
import { 
  Globe, Search, Copy, Check, Filter, Languages, Sparkles, BookOpen, Layers, RefreshCw
} from 'lucide-react';

export default function DictionaryReferenceModule() {
  const { lang, t, setLang, dir } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Collect all unique keys across all dictionaries
  const allKeys = useMemo(() => {
    const keysSet = new Set<string>();
    Object.keys(translations.fa).forEach(k => keysSet.add(k));
    Object.keys(translations.en).forEach(k => keysSet.add(k));
    Object.keys(translations.sv).forEach(k => keysSet.add(k));
    return Array.from(keysSet);
  }, []);

  // Categorize keys logically
  const categorizedKeys = useMemo(() => {
    return allKeys.map(key => {
      let category = 'other';
      if (
        key.startsWith('app') || key === 'dashboard' || key === 'finance' || 
        key === 'reminders' || key === 'shopping' || key === 'news' || 
        key === 'personalDoctor' || key === 'settings' || key.startsWith('cat')
      ) {
        category = 'navigation';
      } else if (
        key.startsWith('finance') || key.includes('Currency') || key.includes('Invoice') || 
        key === 'addTransaction' || key === 'totalIncome' || key === 'totalExpenses' || 
        key === 'netBalance' || key === 'invoices' || key === 'budgets' || key.endsWith('Tab')
      ) {
        category = 'finance';
      } else if (
        key.startsWith('doctor') || key.startsWith('nutrition') || key.startsWith('rehab') || 
        key.startsWith('water') || key.startsWith('calories') || key.startsWith('weight')
      ) {
        category = 'health';
      } else if (key.startsWith('reminder')) {
        category = 'reminders';
      } else if (key.startsWith('shop') || key === 'addItem' || key === 'bought') {
        category = 'shopping';
      } else if (key.startsWith('news')) {
        category = 'news';
      } else if (key.startsWith('setting') || key.startsWith('lang') || key.startsWith('profile')) {
        category = 'settings';
      } else {
        category = 'common';
      }

      return {
        key,
        category,
        fa: translations.fa[key] || '-',
        en: translations.en[key] || '-',
        sv: translations.sv[key] || '-'
      };
    });
  }, [allKeys]);

  const filteredItems = useMemo(() => {
    return categorizedKeys.filter(item => {
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      const lowerSearch = searchTerm.toLowerCase().trim();
      const matchesSearch = !lowerSearch || 
        item.key.toLowerCase().includes(lowerSearch) ||
        item.fa.toLowerCase().includes(lowerSearch) ||
        item.en.toLowerCase().includes(lowerSearch) ||
        item.sv.toLowerCase().includes(lowerSearch);

      return matchesCategory && matchesSearch;
    });
  }, [categorizedKeys, categoryFilter, searchTerm]);

  const handleCopy = (text: string, keyName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleLanguageSwitch = (newLang: Language) => {
    setLang(newLang);
    setAppLanguage(newLang);
  };

  return (
    <div className="space-y-6 font-sans">
      
      {/* Header Banner */}
      <div className="p-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-sky-700 rounded-3xl text-white shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-2xl">
              <Languages className="w-6 h-6 text-indigo-200" />
            </div>
            <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">
              {t('dictionaryTab', 'مرجع متغیرها و ترجمه‌های چندزبانه')}
            </h1>
          </div>
          <p className="text-xs text-indigo-100 opacity-90 max-w-2xl leading-relaxed">
            {t('dictionaryDesc', 'تعریف متمرکز تمام متون، عناوین و کلیدهای برنامه‌نویسی به ۳ زبان فارسی (fa)، انگلیسی (en) و سوئدی (sv) با همگام‌سازی زنده')}
          </p>
        </div>

        {/* Active Language Controls */}
        <div className="flex items-center gap-1.5 p-1.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shrink-0">
          <button
            onClick={() => handleLanguageSwitch('fa')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              lang === 'fa' ? 'bg-white text-indigo-900 shadow-md' : 'text-white hover:bg-white/10'
            }`}
          >
            🇮🇷 فارسی (fa)
          </button>
          <button
            onClick={() => handleLanguageSwitch('en')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              lang === 'en' ? 'bg-white text-indigo-900 shadow-md' : 'text-white hover:bg-white/10'
            }`}
          >
            🇬🇧 English (en)
          </button>
          <button
            onClick={() => handleLanguageSwitch('sv')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              lang === 'sv' ? 'bg-white text-indigo-900 shadow-md' : 'text-white hover:bg-white/10'
            }`}
          >
            🇸🇪 Svenska (sv)
          </button>
        </div>
      </div>

      {/* Stats & Search Bar */}
      <div className="p-5 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                {t('dictionaryTotalKeys', 'کل متغیرهای تعریف شده در دیکشنری:')} <span className="text-indigo-600 dark:text-indigo-400 font-extrabold">{allKeys.length} {t('dictionaryKeysUnit', 'کلید')}</span>
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('dictionaryFilteredResults', 'تعداد نتایج فیلتر شده:')} {filteredItems.length} {t('dictionaryItemsUnit', 'عنوان')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder={t('dictionarySearchPlaceholder', 'جستجو در نام کلید، متن فارسی، انگلیسی یا سوئدی...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-xs text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pt-2 border-t border-gray-100 dark:border-gray-800">
          <Filter className="w-4 h-4 text-gray-400 shrink-0" />
          {[
            { id: 'all', label: t('dictionaryCatAll', 'همه متغیرها') },
            { id: 'finance', label: t('dictionaryCatFinance', 'مدیریت مالی (Finance)') },
            { id: 'navigation', label: t('dictionaryCatNav', 'ناوبری و منوها') },
            { id: 'health', label: t('dictionaryCatHealth', 'سلامت و پزشک') },
            { id: 'reminders', label: t('dictionaryCatReminders', 'یادآورها') },
            { id: 'shopping', label: t('dictionaryCatShopping', 'لیست خرید') },
            { id: 'news', label: t('dictionaryCatNews', 'اخبار و AI') },
            { id: 'settings', label: t('dictionaryCatSettings', 'تنظیمات') },
            { id: 'common', label: t('dictionaryCatCommon', 'عمومی و دکمه‌ها') },
          ].map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                categoryFilter === cat.id
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Dictionary Items Table */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/80 text-gray-500 dark:text-gray-400 font-bold border-b border-gray-200 dark:border-gray-800">
              <tr>
                <th className="p-3.5">{t('dictionaryThKey', 'نام کلید متغیر (Key Name)')}</th>
                <th className="p-3.5">{t('dictionaryThFa', '🇮🇷 فارسی (fa)')}</th>
                <th className="p-3.5">{t('dictionaryThEn', '🇬🇧 انگلیسی (en)')}</th>
                <th className="p-3.5">{t('dictionaryThSv', '🇸🇪 سوئدی (sv)')}</th>
                <th className="p-3.5 text-center">{t('dictionaryThActions', 'عملیات')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredItems.map((item) => (
                <tr key={item.key} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors">
                  <td className="p-3.5 font-mono text-[11px] font-bold text-indigo-600 dark:text-indigo-400">
                    <div className="flex items-center gap-1.5">
                      <span>{item.key}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 font-sans">
                        {item.category}
                      </span>
                    </div>
                  </td>
                  <td className={`p-3.5 font-sans font-medium text-gray-900 dark:text-white ${lang === 'fa' ? 'bg-indigo-50/50 dark:bg-indigo-950/30 font-bold' : ''}`}>
                    {item.fa}
                  </td>
                  <td className={`p-3.5 font-sans font-medium text-gray-800 dark:text-gray-200 ${lang === 'en' ? 'bg-indigo-50/50 dark:bg-indigo-950/30 font-bold' : ''}`}>
                    {item.en}
                  </td>
                  <td className={`p-3.5 font-sans font-medium text-gray-800 dark:text-gray-200 ${lang === 'sv' ? 'bg-indigo-50/50 dark:bg-indigo-950/30 font-bold' : ''}`}>
                    {item.sv}
                  </td>
                  <td className="p-3.5 text-center">
                    <button
                      onClick={() => handleCopy(`t('${item.key}')`, item.key)}
                      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 transition-colors cursor-pointer"
                      title="کپی دستور t('key')"
                    >
                      {copiedKey === item.key ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
